import { useParams, Link } from 'react-router-dom';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory, getHotArticles } from '@/mocks/articles';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import TrendingSidebar from '@/components/base/TrendingSidebar';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? getCategoryBySlug(slug) : undefined;

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-4">
            <i className="ri-emotion-sad-line text-2xl text-foreground-500"></i>
          </span>
          <h2 className="text-xl font-semibold text-foreground-900 mb-2">分类不存在</h2>
          <p className="text-sm text-foreground-600 mb-4">请检查分类链接是否正确</p>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const articles = getArticlesByCategory(category.slug);
  const hotArticles = getHotArticles(6);

  return (
    <div className="min-h-screen pt-16">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: category.name },
          ]}
        />

        <div className="flex flex-col lg:flex-row gap-8 mt-4">
          <div className="flex-1 min-w-0">
            {/* Category Header */}
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 mb-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center">
                  <i className={`${category.icon} text-xl text-primary-500`}></i>
                </span>
                <div>
                  <h1 className="text-2xl font-heading font-bold text-foreground-900">{category.name}</h1>
                  <p className="text-sm text-foreground-600 mt-0.5">{category.description}</p>
                </div>
              </div>
              <p className="text-xs text-foreground-500">
                共 <strong>{articles.length}</strong> 篇相关吃瓜资讯
              </p>
            </div>

            {/* All Categories Quick Nav */}
            <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors cursor-pointer ${
                    cat.slug === category.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Articles List */}
            {articles.length > 0 ? (
              <div className="space-y-3">
                {articles.map((article) => (
                  <ArticleCard key={article.id} article={article} variant="horizontal" />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-inbox-line text-2xl text-foreground-500"></i>
                </span>
                <p className="text-foreground-600">该分类暂无相关吃瓜资讯</p>
                <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors mt-2 inline-block">
                  返回首页查看更多
                </Link>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="w-full lg:w-80 flex-shrink-0">
            <div className="lg:sticky lg:top-20">
              <TrendingSidebar />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}